/**
 * Hourly-rate cell with auto / override / warning states.
 *
 * Extracted from `FinanceTable.tsx` during the master-detail redesign
 * so the detail panel can reuse it. Behaviour is unchanged: shows the
 * resolved rate + source chip, click opens a popover to set or clear
 * a manual override (gated by `canManage`).
 */

import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import { useTranslation } from 'react-i18next';
import type { Designation, HourlyRate } from '@altyor-kronos/shared-types';
import { resolveHourlyRate, type RateResolutionContext } from './rateResolver';

export default function RateCell({
  designation,
  rules,
  context,
  canManage,
  onOverrideCommit,
}: {
  designation: Designation;
  rules: readonly HourlyRate[];
  context: RateResolutionContext;
  canManage: boolean;
  onOverrideCommit: (newOverride: number | null) => void;
}) {
  const { t } = useTranslation();
  const resolution = resolveHourlyRate(context, rules, designation.hourlyRateOverride);
  const cellRef = useRef<HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [overrideValue, setOverrideValue] = useState('');

  const handleOpen = () => {
    if (!canManage) return;
    setOverrideValue(
      designation.hourlyRateOverride != null ? String(designation.hourlyRateOverride) : '',
    );
    setAnchorEl(cellRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCommit = () => {
    const parsed = Number(overrideValue);
    if (overrideValue.trim() !== '' && Number.isFinite(parsed) && parsed > 0) {
      onOverrideCommit(parsed);
    }
    handleClose();
  };

  const handleClear = () => {
    onOverrideCommit(null);
    handleClose();
  };

  const chipLabel =
    resolution.source === 'override'
      ? t('finance.rate.override')
      : resolution.source === 'rule' || resolution.source === 'default'
        ? t('finance.rate.auto')
        : t('finance.rate.noRule');

  const chipColor: 'default' | 'warning' =
    resolution.source === 'none' ? 'warning' : 'default';

  const chipTooltip =
    resolution.source === 'rule' && resolution.rule ? resolution.rule.label : '';

  const amountLabel =
    resolution.amount > 0
      ? `${resolution.amount} €/h`
      : '—';

  return (
    <>
      <Box
        ref={cellRef}
        component="span"
        onClick={handleOpen}
        sx={{ cursor: canManage ? 'pointer' : 'default', display: 'inline-block' }}
        data-testid={`finance-rate-cell-${designation.id}`}
      >
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <span>{amountLabel}</span>
          {chipTooltip ? (
            <Tooltip title={chipTooltip}>
              <Chip size="small" label={chipLabel} color={chipColor} />
            </Tooltip>
          ) : (
            <Chip size="small" label={chipLabel} color={chipColor} />
          )}
        </Stack>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('finance.rate.popoverResolvedLabel')}: {resolution.amount} €/h
            {resolution.rule ? ` (${resolution.rule.label})` : ''}
          </Typography>
          <TextField
            size="small"
            type="number"
            label={t('finance.rate.popoverOverrideLabel')}
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCommit();
              if (e.key === 'Escape') handleClose();
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={handleCommit}>
              <CheckIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={handleClear}
            >
              {t('finance.rate.clearOverride')}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
